// View helpers for AI import drafts. The backend decides everything that
// matters (validation, issues, blocking problems); these only read it.

import { menuErrorMessage } from "../menuRole";
import { aiWalletErrorText } from "../../billing/aiWallet/AiBalanceNotice";

export const LOW_CONFIDENCE = 0.6;
export const ACTIVE_STATUSES = ["uploaded", "processing", "approving"];

export const STATUS_VARIANT = { uploading: "info", uploaded: "info", processing: "info", needs_review: "warning", approving: "info", approved: "success", archived: "neutral", failed: "danger" };
export const PROVENANCE_VARIANT = { EXTRACTED: "neutral", AI_INFERRED: "info", AI_SUGGESTED: "warning", USER_VERIFIED: "success" };

export const isAiField = (field) => field?.provenance === "AI_INFERRED" || field?.provenance === "AI_SUGGESTED";
export const hasValue = (field) => {
  const value = field?.value;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.values(value).some((v) => (Array.isArray(v) ? v.length : v !== null && v !== undefined && v !== ""));
  return true;
};
export const isLow = (field) => Boolean(field) && field.provenance !== "USER_VERIFIED" && hasValue(field) && field.confidence < LOW_CONFIDENCE;
export const needsCheck = (field) => Boolean(field?.needsVerification) && field.provenance !== "USER_VERIFIED";
export const percent = (confidence) => Math.round((Number(confidence) || 0) * 100);

export const pairText = (pair, lang) => {
  const ar = pair?.ar?.value || "";
  const en = pair?.en?.value || "";
  return (lang === "ar" ? ar || en : en || ar) || "";
};

const itemFields = (item) => [item.name.ar, item.name.en, item.description.ar, item.description.en, item.price, item.discount, item.calories, item.allergens];

// Review filters (the "Additions" filter switches to the additions list).
export function matchesFilter(item, filter, sectionsByKey) {
  const excluded = item.status === "exclude";
  if (filter === "excluded") return excluded;
  if (filter === "all") return true;
  if (excluded) return false;
  const fields = itemFields(item);
  switch (filter) {
    case "extracted":
      return item.name.ar.provenance !== "AI_SUGGESTED" && item.name.en.provenance !== "AI_SUGGESTED" && (hasValue(item.name.ar) ? item.name.ar.provenance === "EXTRACTED" : item.name.en.provenance === "EXTRACTED");
    case "ai":
      return fields.some((f) => isAiField(f) && hasValue(f)) || sectionsByKey.get(item.sectionKey)?.origin === "AI_INFERRED";
    case "verify":
      return fields.some(needsCheck) || (item.issues || []).some((code) => /PRICE_UNRESOLVED|NEEDS_VERIFICATION|NOT_IN_SOURCE|NAME_REQUIRED/.test(code));
    case "low":
      return (item.issues || []).includes("LOW_CONFIDENCE");
    case "duplicates":
      return Boolean(item.duplicate?.candidates?.length);
    default:
      return true;
  }
}

export function filterCounts(proposal) {
  if (!proposal) return {};
  const sections = new Map(proposal.sections.map((s) => [s.key, s]));
  const count = (filter) => proposal.items.filter((item) => matchesFilter(item, filter, sections)).length;
  return { all: proposal.items.length, extracted: count("extracted"), ai: count("ai"), verify: count("verify"), low: count("low"), duplicates: count("duplicates"), additions: proposal.additions.length, excluded: count("excluded") };
}

export function formatMoney(value, t) {
  if (value === null || value === undefined || value === "") return t.empty;
  return `${Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${t.sar}`;
}

export function aiImportErrorMessage(error, t, lang) {
  // AI Wallet refusals: the text for this user, in the page language.
  const wallet = aiWalletErrorText(error, lang);
  if (wallet) return wallet;
  return t.errors[error?.code] || menuErrorMessage(error, lang);
}

// Current file name (without extension) as a default menu name.
export const baseName = (fileName) => String(fileName || "").replace(/\.[^.]+$/, "").slice(0, 120);

// Mirrors lud-backend constants/ai/menuAllergens.js (codes are validated there).
export const ALLERGENS = {
  gluten: { en: "Gluten", ar: "جلوتين" },
  dairy: { en: "Dairy", ar: "ألبان" },
  eggs: { en: "Eggs", ar: "بيض" },
  tree_nuts: { en: "Tree nuts", ar: "مكسرات" },
  peanuts: { en: "Peanuts", ar: "فول سوداني" },
  soy: { en: "Soy", ar: "صويا" },
  fish: { en: "Fish", ar: "سمك" },
  crustaceans: { en: "Crustaceans", ar: "قشريات" },
  molluscs: { en: "Molluscs", ar: "رخويات" },
  sesame: { en: "Sesame", ar: "سمسم" },
  mustard: { en: "Mustard", ar: "خردل" },
  celery: { en: "Celery", ar: "كرفس" },
  sulphites: { en: "Sulphites", ar: "كبريتيت" },
  lupin: { en: "Lupin", ar: "ترمس" },
};
export const allergenLabel = (code, lang) => ALLERGENS[code]?.[lang] || code;
